import type { WhitelabelConfig } from "@nlc/shared-types";
import type { ColumnDef } from "@tanstack/react-table";
import type { SortDescriptor } from "react-stately";

import { memo, useCallback, useMemo, useRef } from "react";

import { Link } from "@/components/link";
import {
  type PaginationMetadata,
  DraggableTable as Table,
} from "@/components/table";
import { useTableSorting } from "@/hooks/useTableSorting";
import { dateFormatter } from "@/utils/dateFormatter";

const COLUMN_WIDTHS = {
  CREATED_AT: 140,
  CUSTOM_DOMAIN: 200,
  IS_ACTIVE: 100,
  OWNER_ID: 100,
  PARTNER_NAME: 250,
};

interface WhitelabelConfigsTableProps {
  configs: WhitelabelConfig[];
  isLoading?: boolean;
  pagination?: {
    metadata: PaginationMetadata;
    onItemsPerPageChange?: (itemsPerPage: number) => void;
    onPageChange: (page: number) => void;
    pageSizeOptions?: number[];
    showPageSizeSelector?: boolean;
  };
  setSortDescriptor: (sortDescriptor: SortDescriptor) => void;
  sortDescriptor: SortDescriptor;
}

const PartnerNameCell = memo(function PartnerNameCell({
  id,
  name,
}: {
  id: number;
  name: string;
}) {
  return (
    <Link className="hover:no-underline" href={`/whitelabel/${id}/edit`}>
      <div className="flex items-center py-8 min-w-0 w-full overflow-hidden">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-text-primary leading-tight min-w-0 line-clamp-2 break-words">
            {name}
          </p>
        </div>
      </div>
    </Link>
  );
});

const CustomDomainCell = memo(function CustomDomainCell({
  domain,
}: {
  domain: null | string;
}) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <span className="text-text-tertiary text-sm">{domain ?? "—"}</span>
    </div>
  );
});

const OwnerIdCell = memo(function OwnerIdCell({
  ownerId,
}: {
  ownerId: number;
}) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <Link
        className="text-text-tertiary text-sm"
        href={`/users/${ownerId}/edit`}
      >
        {ownerId}
      </Link>
    </div>
  );
});

const IsActiveCell = memo(function IsActiveCell({
  isActive,
}: {
  isActive: boolean;
}) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <span
        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
          isActive ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
        }`}
      >
        {isActive ? "Active" : "Inactive"}
      </span>
    </div>
  );
});

const CreatedAtCell = memo(function CreatedAtCell({
  createdAt,
}: {
  createdAt: null | string;
}) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <span className="text-text-tertiary text-sm">
        {createdAt ? dateFormatter(createdAt) : "—"}
      </span>
    </div>
  );
});

export const WhitelabelConfigsTable = memo(function WhitelabelConfigsTable({
  configs,
  isLoading = false,
  pagination,
  setSortDescriptor,
  sortDescriptor,
}: WhitelabelConfigsTableProps) {
  const tableRef = useRef<HTMLDivElement>(null);

  const { handleSort, sorting } = useTableSorting({
    defaultColumn: "createdAt",
    defaultDirection: "descending",
    setSortDescriptor,
    sortDescriptor,
  });

  const columns: ColumnDef<WhitelabelConfig>[] = useMemo(
    () => [
      {
        accessorKey: "partnerName",
        cell: ({ getValue, row }) => (
          <PartnerNameCell id={row.original.id} name={getValue() as string} />
        ),
        enableSorting: true,
        header: "Partner Name",
        maxSize: COLUMN_WIDTHS.PARTNER_NAME,
        minSize: COLUMN_WIDTHS.PARTNER_NAME,
        size: COLUMN_WIDTHS.PARTNER_NAME,
      },
      {
        accessorKey: "customDomain",
        cell: ({ getValue }) => (
          <CustomDomainCell domain={getValue() as null | string} />
        ),
        enableSorting: true,
        header: "Custom Domain",
        maxSize: COLUMN_WIDTHS.CUSTOM_DOMAIN,
        minSize: COLUMN_WIDTHS.CUSTOM_DOMAIN,
        size: COLUMN_WIDTHS.CUSTOM_DOMAIN,
      },
      {
        accessorKey: "ownerId",
        cell: ({ getValue }) => <OwnerIdCell ownerId={getValue() as number} />,
        enableSorting: true,
        header: "Owner ID",
        maxSize: COLUMN_WIDTHS.OWNER_ID,
        minSize: COLUMN_WIDTHS.OWNER_ID,
        size: COLUMN_WIDTHS.OWNER_ID,
      },
      {
        accessorKey: "isActive",
        cell: ({ getValue }) => (
          <IsActiveCell isActive={getValue() as boolean} />
        ),
        enableSorting: true,
        header: "Status",
        maxSize: COLUMN_WIDTHS.IS_ACTIVE,
        minSize: COLUMN_WIDTHS.IS_ACTIVE,
        size: COLUMN_WIDTHS.IS_ACTIVE,
      },
      {
        accessorKey: "createdAt",
        cell: ({ getValue }) => (
          <CreatedAtCell createdAt={getValue() as null | string} />
        ),
        enableSorting: true,
        header: "Created",
        maxSize: COLUMN_WIDTHS.CREATED_AT,
        minSize: COLUMN_WIDTHS.CREATED_AT,
        size: COLUMN_WIDTHS.CREATED_AT,
      },
    ],
    [],
  );

  const getRowId = useCallback(
    (config: WhitelabelConfig) => config.id.toString(),
    [],
  );

  const paginationConfig = useMemo(
    () =>
      pagination
        ? {
            metadata: pagination.metadata,
            onItemsPerPageChange: pagination.onItemsPerPageChange,
            onPageChange: pagination.onPageChange,
            pageSizeOptions: pagination.pageSizeOptions,
            showPageSizeSelector: pagination.showPageSizeSelector,
          }
        : undefined,
    [
      pagination?.metadata,
      pagination?.onItemsPerPageChange,
      pagination?.onPageChange,
      pagination?.pageSizeOptions,
      pagination?.showPageSizeSelector,
    ],
  );

  const sortableColumns = useMemo(
    () => ["partnerName", "customDomain", "ownerId", "isActive", "createdAt"],
    [],
  );

  return (
    <div className="rounded-b-xl bg-bg-primary overflow-auto" ref={tableRef}>
      <Table
        className="rounded-b-xl w-full"
        columns={columns}
        data={configs}
        getRowId={getRowId}
        isLoading={isLoading}
        onSort={handleSort}
        pagination={paginationConfig}
        sortableColumns={sortableColumns}
        sorting={sorting}
      />
    </div>
  );
});
